/** Inline new-task create/cancel handlers for CalendarView double-click slots. */
import { useRef, useState, useCallback } from 'react';
import { format } from 'date-fns';
import { DEFAULT_TASK_DURATION_MINS } from '@/config/constants';

export function useCalendarNewTaskHandlers({
  allTasks,
  user,
  addTask,
  augmentNewTaskWithDefaults,
}) {
  const [newTaskInfo, setNewTaskInfo] = useState(null);
  const pendingScheduleRef = useRef(null);

  const clearPendingSchedule = useCallback(() => {
    pendingScheduleRef.current = null;
    setNewTaskInfo(null);
  }, []);

  const handleDoubleClick = useCallback((day, time) => {
    const schedule = {
      date: format(day.date, 'yyyy-MM-dd'),
      time,
    };
    pendingScheduleRef.current = schedule;
    setNewTaskInfo({
      ...schedule,
      title: '',
      duration: DEFAULT_TASK_DURATION_MINS,
    });
  }, []);

  const handleSaveTask = useCallback(
    async (taskData) => {
      const schedule = pendingScheduleRef.current;
      if (!schedule?.date || !schedule?.time) {
        throw new Error('Task schedule info is missing.');
      }
      if (!user?.uid) {
        throw new Error('User not logged in.');
      }

      const initialPayload = {
        ...taskData,
        date: schedule.date,
        time: schedule.time,
        duration: DEFAULT_TASK_DURATION_MINS,
        requireTimeReview: true,
      };

      const finalTaskPayload = augmentNewTaskWithDefaults(
        initialPayload,
        allTasks,
      );
      await addTask(finalTaskPayload);
      clearPendingSchedule();
    },
    [addTask, allTasks, augmentNewTaskWithDefaults, clearPendingSchedule, user],
  );

  const handleCancel = useCallback(() => {
    clearPendingSchedule();
  }, [clearPendingSchedule]);

  return {
    newTaskInfo,
    handleDoubleClick,
    handleSaveTask,
    handleCancel,
  };
}
